import React, { useContext } from "react";
import { useQuery } from "@apollo/client/react/hooks/useQuery.js";
import { useApolloClient } from "@apollo/client/react/hooks/useApolloClient.js";
import { NetworkStatus } from "@apollo/client/core/networkStatus.js";
import { useParams } from "react-router";
import {
    GetGameData,
    GetGameVariables,
    PickGameQuestionVariantVariables,
    SaveGameResultVariables,
} from "../../../graphql/queries/games/_common.js";
import {
    GET_QUIZ,
    PICK_QUIZ_QUESTION_VARIANT,
    Quiz,
    SAVE_QUIZ_RESULT,
} from "../../../graphql/queries/games/quiz.js";
import { Content } from "../../layout/CommonContentContextProvider.js";
import ErrorMsg from "../../common/errors/ErrorMsg.js";
import { container } from "../../styles/common.css.js";
import Spinner from "../../Spinner.js";
import MetaTags from "../../MetaTags.js";
import QuizContainer from "./QuizContainer.js";
import Breadcrumbs from "../../common/Breadcrumbs.js";
import HtmlBreadcrumbsStructuredData from "../../helmet/HtmlBreadcrumbsStructuredData.js";
import NotFoundErrorMsg from "../../common/errors/NotFoundErrorMsg.js";
import { ConfigContext } from "../../AppConfigProvider.js";
// import GoogleRailAdunit from "../../common/ads/GoogleRailAdUnit.js";

const QuizLoader: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const {
        langCode,
        common: { games },
    } = useContext(Content);
    const { appleAppMarketId } = useContext(ConfigContext);
    const { data, loading, error, refetch, networkStatus } = useQuery<
        GetGameData<Quiz>,
        GetGameVariables
    >(GET_QUIZ, {
        variables: {
            id,
            langCode,
        },
        notifyOnNetworkStatusChange: true,
    });

    const apolloClient = useApolloClient();

    const pickQuizQuestionVariant = async (variantId: string) => {
        await apolloClient.mutate<any, PickGameQuestionVariantVariables>({
            mutation: PICK_QUIZ_QUESTION_VARIANT,
            variables: {
                input: {
                    quizId: id,
                    variantId,
                },
            },
        });
    };
    const saveQuizResult = async (quizId: string, result: number) => {
        await apolloClient.mutate<any, SaveGameResultVariables>({
            mutation: SAVE_QUIZ_RESULT,
            variables: {
                input: {
                    quizId,
                    result,
                },
            },
        });
    };

    if (error) {
        return <ErrorMsg error={error} />;
    }

    if (data && data.game && data.game.id === id) {
        const { game } = data;
        const path = `/quiz/${game.id}`;
        const containerClass = "flex680";

        return (
            <>
                <MetaTags
                    currentLang={langCode}
                    langs={game.contentLangs}
                    path={path}
                    metaTitle={game.content.metaTitle}
                    title={game.content.title}
                    description={game.content.metaDescription}
                    promoAppId={appleAppMarketId}
                    mediaItem={game.content.image}
                    keywords={game.content.keywords}
                    largeImagePreview
                />
                <HtmlBreadcrumbsStructuredData
                    pageTitle={game.content.title}
                    sectionName="games"
                />
                <Breadcrumbs
                    url={`/${langCode}/games`}
                    title={games}
                    location="games"
                    containerClass={containerClass}
                />
                <div className={container[containerClass]}>
                    <QuizContainer
                        key={game.id}
                        quiz={game}
                        pagePath={path}
                        onPickVariant={pickQuizQuestionVariant}
                        refresh={refetch}
                        refreshing={networkStatus === NetworkStatus.refetch}
                        saveResult={saveQuizResult}
                    />
                </div>
                {/* <GoogleRailAdunit id="quiz-page-left-rail" /> */}
                {/* <GoogleRailAdunit id="quiz-page-right-rail" /> */}
            </>
        );
    }

    if (loading) {
        return <Spinner />;
    }

    return <NotFoundErrorMsg />;
};

export default QuizLoader;
